import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { acceptBudgetRecommendation } from '@/api-clients/accept-budget-recommendation';
import { useToaster } from '@/components/base/toaster';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';
import { useRecommendationContext } from '@/contexts/recommendation-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { durationInDays, formatShortDate } from '@/utils/date-util';

import { useQueryInvalidation } from '../use-query-invalidation';
import { useBudgetRecommendationSchema } from '../validation/budget-recommendation-schema';
import { BudgetEdit } from './budget-edit';

import type {
  BudgetRecommendationFormFields,
  HiringGoalDetails,
  ValidatedBudgetRecommendationFields,
} from '../types';
import type { BudgetRecommendation } from 'api-clients/monolith';

interface BudgetRecommendationFormProps {
  hiringGoal?: HiringGoalDetails;
  budgetRecommendation: BudgetRecommendation;
}
export function BudgetRecommendationForm({
  hiringGoal,
  budgetRecommendation,
}: BudgetRecommendationFormProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { notifySuccess, notifyError } = useToaster();
  const { setIsSubmitting, closeRecommendationPostingDialog } =
    useRecommendationContext();
  const { invalidateRecommendationPostingQueries } = useQueryInvalidation();

  const existingBudget = budgetRecommendation.sourcing_purchase.budget;
  const newBudget = budgetRecommendation.budget + existingBudget;
  const duration = durationInDays(
    budgetRecommendation.sourcing_purchase.starts_at,
    budgetRecommendation.sourcing_purchase.end_at,
  );

  const channelSlug = budgetRecommendation.sourcing_channel.slug;
  const budgetValidationSchema = useBudgetRecommendationSchema(channelSlug);

  const hookForm = useForm<
    BudgetRecommendationFormFields,
    unknown,
    ValidatedBudgetRecommendationFields
  >({
    resolver: zodResolver(budgetValidationSchema),
    defaultValues: {
      budget: newBudget,
    },
  });
  const { handleSubmit } = hookForm;

  const { mutate: acceptBudgetRecommendationMutation } = useMutation({
    mutationFn: acceptBudgetRecommendation,
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Campaign updated successfully!',
          description:
            'Toast after saving an accepted budget recommendation for a channel',
        }),
      });

      setIsSubmitting(false);
      await invalidateRecommendationPostingQueries();

      closeRecommendationPostingDialog();
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update campaign!',
          description:
            'Toast when the API could not apply the budget recommendation change',
        }),
      });
      setIsSubmitting(false);
    },
  });

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    void handleSubmit(({ budget }) =>
      acceptBudgetRecommendationMutation({
        sourcingPurchaseId: budgetRecommendation.sourcing_purchase.external_id,
        requestBody: {
          recommended_budget: budget,
        },
      }),
    )();
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Typography variant="body2" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Adjust budget for"
              description="Lead-in before the board icon in the budget recommendation dialog"
            />
          </Typography>

          <ChannelIcon
            variant="large"
            size="sm"
            channelSlug={budgetRecommendation.sourcing_channel.slug}
            channelName={budgetRecommendation.sourcing_channel.name}
          />
        </div>
        {hiringGoal && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Hiring goal end date : {endDate} ({daysRemaining} days remaining)"
              description="Shows goal deadline and days left in the budget review dialog"
              values={{
                endDate: formatShortDate(hiringGoal.hireBy),
                daysRemaining: hiringGoal.duration - hiringGoal.daysElapsed,
              }}
            />
          </Typography>
        )}

        <form id="recommendation-form" onSubmit={handleFormSubmit}>
          <div className="flex items-center gap-2">
            {/* Current budget and duration */}
            <Typography variant="small1" className="!text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="Current: {budget} / {duration} days"
                description="Existing campaign spend and day range before the proposed budget"
                values={{
                  budget: formatCurrency(existingBudget),
                  duration,
                }}
              />
            </Typography>

            <ArrowRight className="size-4 text-fx-neutral-600" />

            <BudgetEdit duration={duration} hookForm={hookForm} />
          </div>
        </form>
      </div>
    </>
  );
}
